import { createApi } from "@reduxjs/toolkit/query/react";
import {
  baseQueryWithArgs,
  getQueryStringWithCreator,
  postQueryBodyWithCreator,
} from "../../../utils/reduxHelper";
import {
  hideToastNotification,
  showToastNotification,
} from "../../../redux/redux";
import { store } from "../../../redux/redux";

/**
 * Whitelisting Redux API
 *
 * Cache invalidation strategy:
 * 1. refetchOnMountOrArgChange: true - Ensures data is refetched when components mount during navigation
 * 2. keepUnusedDataFor: 0 - Immediately invalidates cache when not in use
 * 3. refetchOnFocus: true - Refetches data when window regains focus
 * 4. refetchOnReconnect: true - Refetches data when network reconnects
 * 5. Mutations invalidate relevant tags to trigger refetches
 */

export const sourcingWhitelistingReduxAPI = createApi({
  reducerPath: "sourcingWhitelistingReduxAPI",
  baseQuery: baseQueryWithArgs,
  refetchOnMountOrArgChange: true, // Refetch when component mounts or arguments change
  refetchOnReconnect: true, // Refetch when network reconnects

  tagTypes: ["getWhitelistings", "addWhitelistingProduct"],

  endpoints: (builder) => ({
    //GET Queries
    getWhitelistings: builder.query({
      query: ({ whitelistingEligibilityId, isFirstVisit }) =>
        getQueryStringWithCreator(
          `s/getWhitelistings?whitelistingEligibilityId=${whitelistingEligibilityId}&isFirstVisit=${isFirstVisit}`
        ),
      providesTags: ["getWhitelistings"],
      refetchOnMountOrArgChange: true, // Ensure refetch on component mount or when arguments change
      keepUnusedDataFor: 0, // Don't keep data in cache when not in use (immediate invalidation)
    }),

    // POST Queries
    placeWhitelistingRequest: builder.mutation({
      query: (body) => ({
        url: "s/placeWhitelistingRequest",
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
      invalidatesTags: ["getWhitelistings"],
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const response = await queryFulfilled;
          if (response?.data?.success) {
            showToast({
              variant: "success",
              message: "Form Submitted",
            });
          } else {
            const errorMsg =
              response?.data?.msg ||
              response?.data?.error ||
              "Oops! error in getting brands whitelisting data";
            showToast({
              variant: "error",
              message: errorMsg,
            });
          }
        } catch (error) {
          const errorMsg =
            error?.error?.data?.msg ||
            error?.error?.data?.error ||
            "Oops! error in getting brands whitelisting data";
          showToast({
            variant: "error",
            message: errorMsg,
          });
        }
      },
    }),
  }),
});

function showToast({ variant, message }) {
  store.dispatch(showToastNotification({ variant, message }));
  setTimeout(() => {
    store.dispatch(hideToastNotification());
  }, 3000);
}

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useGetWhitelistingsQuery, usePlaceWhitelistingRequestMutation } =
  sourcingWhitelistingReduxAPI;
